// Tạo file mới: src/components/SearchDialog.js

import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, Check } from 'lucide-react';

const SearchDialog = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  items = [], 
  title = "Chọn", 
  searchPlaceholder = "Tìm kiếm...",
  displayField = "name",
  valueField = "id",
  secondaryField = null,
  selectedValue = null,
  allowEmpty = true,
  emptyText = "Không chọn"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(selectedValue);

  // Reset search when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSelectedItem(selectedValue);
    }
  }, [isOpen, selectedValue]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    
    const term = searchTerm.toLowerCase();
    return items.filter(item => {
      const displayText = item[displayField]?.toString().toLowerCase() || '';
      const secondaryText = secondaryField ? (item[secondaryField]?.toString().toLowerCase() || '') : '';
      
      return displayText.includes(term) || secondaryText.includes(term);
    });
  }, [items, searchTerm, displayField, secondaryField]);

  const handleSelect = (item) => {
    setSelectedItem(item[valueField]);
    onSelect(item);
    onClose();
  };

  const handleClear = () => {
    setSelectedItem(null);
    onSelect(null);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
          
          {/* Clear selection button */}
          {allowEmpty && (
            <button
              onClick={handleClear}
              className="mt-3 w-full text-left px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {emptyText}
            </button>
          )}
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto max-h-96">
          {filteredItems.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {filteredItems.map((item, index) => {
                const isSelected = selectedItem === item[valueField];
                
                return (
                  <button
                    key={item[valueField] || index}
                    onClick={() => handleSelect(item)}
                    className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors ${
                      isSelected ? 'bg-blue-100 border-r-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {item[displayField]}
                        </div>
                        {secondaryField && item[secondaryField] && (
                          <div className="text-sm text-gray-500 truncate mt-1">
                            {item[secondaryField]}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5 text-blue-600 ml-2 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm">
                {searchTerm.trim() 
                  ? `Không tìm thấy kết quả cho "${searchTerm}"` 
                  : 'Không có dữ liệu'
                }
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 text-center">
            {filteredItems.length > 0 && (
              `Hiển thị ${filteredItems.length} / ${items.length} mục`
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchDialog;